import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import SiteHeader from "@/components/SiteHeader";
import VoiceRecorder from "@/components/VoiceRecorder";
import CrisisModal from "@/components/CrisisModal";
import { PageMeta } from "@/lib/seo";
import ROUTE_META from "@/lib/routeMeta.json";
import { trackTryStartIfFromBar } from "@/lib/barTracking";
import { hc } from "@/lib/heycatch";
import TrustpilotAsk from "@/components/TrustpilotAsk";
import ChapterWelcome from "@/components/ChapterWelcome";
import SpeakButton from "@/components/SpeakButton";
import { useFounderVoicedPrompts } from "@/lib/founderVoice";
import { useRecorder } from "@/lib/useRecorder";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useEntitlements } from "@/lib/entitlements";
import { createEntry, fetchPreviewChapter, requestPreviewReflection, transcribePreviewAudio } from "@/lib/api";

const STORE_KEY = "umbra_preview_ch1";
const NUDGE_KEY = "umbra_preview_nudge_dismissed";
const ENTITLED_TIERS = ["listener_lifetime", "grandfathered_lifetime", "admin"];

function loadDraft() {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || "{}");
  } catch {
    return {};
  }
}

function KeepNudge({ onDismiss }) {
  const { user } = useAuth();
  const { entitlements } = useEntitlements();
  const entitled = user && entitlements && (entitlements.subscription_active || ENTITLED_TIERS.includes(entitlements.tier));
  const cta = "inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-5 min-h-[44px] font-sans text-xs uppercase tracking-[0.22em] transition-colors";
  return (
    <aside className="mt-6 border border-terracotta/40 bg-obsidian-800/80 p-5 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-up" data-testid="try-keep-nudge">
      <div className="flex-1">
        <p className="font-serif text-lg text-bone-100">Keep these answers?</p>
        <p className="font-sans text-sm text-bone-400 leading-relaxed">Right now they only live in this tab. {entitled ? "Save them to your journal before you go." : "Subscribe and they move into your private journal."}</p>
      </div>
      {entitled ? (
        <button onClick={() => document.getElementById("try-save-bar")?.scrollIntoView({ behavior: "smooth" })} data-testid="try-keep-nudge-save" className={cta}>Save them</button>
      ) : (
        <Link to="/pricing" data-testid="try-keep-nudge-pricing" className={cta}>Keep these answers <ArrowRight className="h-4 w-4" /></Link>
      )}
      <button onClick={onDismiss} aria-label="Dismiss" data-testid="try-keep-nudge-dismiss" className="self-start sm:self-center text-bone-500 hover:text-bone-100 p-2">
        <X className="h-4 w-4" />
      </button>
    </aside>
  );
}

function PromptCard({ index, prompt, value, onChange, onCrisis, nudge, hasVoice }) {
  const [reflecting, setReflecting] = useState(false);
  const onTranscript = useCallback(
    (text) => onChange({ ...value, answer: [value.answer, text].filter(Boolean).join(" ") }),
    [value, onChange]
  );
  const recorder = useRecorder({ onTranscript, transcribe: transcribePreviewAudio });

  const reflect = async () => {
    setReflecting(true);
    try {
      const r = await requestPreviewReflection(prompt.id, value.answer);
      onChange({ ...value, reflection: r.reflection, reflected_at: value.reflected_at || Date.now() });
      trackTryStartIfFromBar();
      if (!window.sessionStorage.getItem("umbra_hc_try")) {
        window.sessionStorage.setItem("umbra_hc_try", "1");
        hc("trackEvent", "chapter_one_tried", { prompt_id: prompt.id });
      }
      if (r.distress) onCrisis();
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "You've used the preview's reflections for now — try again in an hour." : "The Mirror couldn't answer just now.");
    } finally {
      setReflecting(false);
    }
  };

  return (
    <article className="border-t border-obsidian-600 pt-10" data-testid={`try-prompt-${prompt.id}`}>
      <p className="font-sans text-xs uppercase tracking-[0.28em] text-terracotta mb-3">Prompt {index + 1}</p>
      <div className="flex items-start gap-4 mb-8">
        <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 leading-snug flex-1">{prompt.text}</h2>
        {hasVoice && (
          <SpeakButton text={prompt.text} founderClipId={prompt.id} founderOnly testId={`try-speak-${prompt.id}`} label="Read prompt aloud in Uncle Danny's voice" />
        )}
      </div>
      <VoiceRecorder promptId={prompt.id} recording={recorder.recording} transcribing={recorder.transcribing} onStart={recorder.start} onStop={recorder.stop} />
      <Textarea
        value={value.answer || ""}
        onChange={(e) => onChange({ ...value, answer: e.target.value })}
        placeholder="Speak above, or type here…"
        data-testid={`try-answer-${prompt.id}`}
        className="mt-6 min-h-[120px] bg-obsidian-800 border-obsidian-600 rounded-none font-sans text-base text-bone-100 placeholder:text-bone-500 focus:border-terracotta"
      />
      <Button
        onClick={reflect}
        disabled={!value.answer?.trim() || reflecting}
        data-testid={`try-reflect-${prompt.id}`}
        className="mt-4 rounded-none bg-transparent border border-bone-500 text-bone-100 hover:border-terracotta hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs"
      >
        {reflecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
        {value.reflection ? "Ask the Mirror again" : "Hear the Mirror"}
      </Button>
      {value.reflection && (
        <blockquote className="mt-6 border-l-2 border-terracotta pl-5 font-serif italic text-lg text-bone-200 leading-relaxed animate-fade-up" data-testid={`try-reflection-${prompt.id}`}>
          {value.reflection}
        </blockquote>
      )}
      {value.reflection && nudge}
    </article>
  );
}

function SaveBar({ chapter, draft }) {
  const { user } = useAuth();
  const { entitlements, loading } = useEntitlements();
  const [saving, setSaving] = useState(false);
  const answered = chapter.prompts.filter((p) => draft[p.id]?.answer?.trim());
  const entitled = entitlements && (entitlements.subscription_active || ENTITLED_TIERS.includes(entitlements.tier));

  const track = (action) => hc("trackEvent", "save_to_account_clicked", { action, answered: answered.length, signed_in: !!user });

  const save = async () => {
    track("save");
    setSaving(true);
    try {
      for (const p of answered) {
        const d = draft[p.id];
        await createEntry({ section_id: chapter.id, prompt_id: p.id, prompt_text: p.text, answer: d.answer, reflection: d.reflection || null });
      }
      window.sessionStorage.removeItem(STORE_KEY);
      toast.success("Saved to your journal.");
      window.location.href = `/section/${chapter.id}`;
    } catch {
      toast.error("Could not save right now. Your answers are still here.");
      setSaving(false);
    }
  };

  return (
    <aside id="try-save-bar" className="mt-16 border border-terracotta/50 bg-terracotta/[0.06] p-6 sm:p-8" data-testid="try-save-bar">
      <p className="font-serif text-xl text-bone-100 mb-2">
        {answered.length ? `${answered.length} of ${chapter.prompts.length} answers in this tab.` : "Nothing is saved yet."}
      </p>
      <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6 max-w-xl">
        This preview lives only in this browser tab. Close it and it's gone. Answers save to a private journal once you subscribe.
      </p>
      {user && entitled ? (
        <Button onClick={save} disabled={!answered.length || saving} data-testid="try-save-btn" className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-wider text-xs py-6 px-6">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save to my journal
        </Button>
      ) : user && !loading ? (
        <Link to="/pricing" onClick={() => track("subscribe")} data-testid="try-subscribe-link" className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-6 min-h-[48px] font-sans text-xs uppercase tracking-[0.22em]">
          Subscribe to keep these <ArrowRight className="h-4 w-4" />
        </Link>
      ) : (
        <div className="flex flex-wrap gap-4">
          <Link to="/pricing" onClick={() => track("see_plans")} data-testid="try-pricing-link" className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-6 min-h-[48px] font-sans text-xs uppercase tracking-[0.22em]">
            See the three plans <ArrowRight className="h-4 w-4" />
          </Link>
          <button onClick={() => { track("sign_in"); startGoogleLogin(); }} data-testid="try-signin-btn" className="min-h-[48px] px-2 font-sans text-xs uppercase tracking-[0.22em] text-bone-300 hover:text-terracotta">
            Already subscribed? Sign in
          </button>
        </div>
      )}
    </aside>
  );
}

export default function TryChapterOne() {
  const [chapter, setChapter] = useState(null);
  const [draft, setDraft] = useState(loadDraft);
  const [crisisOpen, setCrisisOpen] = useState(false);
  const voiced = useFounderVoicedPrompts();
  const [nudgeDismissed, setNudgeDismissed] = useState(() => window.sessionStorage.getItem(NUDGE_KEY) === "1");
  const reflectedIds = Object.entries(draft)
    .filter(([, v]) => v?.reflection)
    .sort(([, a], [, b]) => (a.reflected_at || 0) - (b.reflected_at || 0))
    .map(([id]) => id);
  const nudgeId = !nudgeDismissed && reflectedIds.length >= 2 ? reflectedIds[1] : null;
  const dismissNudge = () => {
    window.sessionStorage.setItem(NUDGE_KEY, "1");
    setNudgeDismissed(true);
  };

  useEffect(() => {
    fetchPreviewChapter().then(setChapter).catch(() => toast.error("Couldn't open Chapter One."));
  }, []);

  useEffect(() => {
    window.sessionStorage.setItem(STORE_KEY, JSON.stringify(draft));
  }, [draft]);

  const update = useCallback((id) => (v) => setDraft((d) => ({ ...d, [id]: v })), []);

  return (
    <div className="min-h-screen bg-obsidian-900">
      <PageMeta
        {...ROUTE_META["/try"]}
        path="/try"
      />
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 sm:px-8 pt-14 sm:pt-20 pb-20">
        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-5" data-testid="try-eyebrow">
          Chapter One · no account needed
        </p>
        {!chapter ? (
          <Loader2 className="h-6 w-6 animate-spin text-bone-400" />
        ) : (
          <>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bone-100 mb-6" data-testid="try-title">{chapter.title}</h1>
            <p className="font-serif italic text-lg text-bone-300 leading-relaxed mb-14">{chapter.description}</p>
            <ChapterWelcome sectionId="inner-child" />
            <div className="space-y-14">
              {chapter.prompts.map((p, i) => (
                <PromptCard key={p.id} index={i} prompt={p} value={draft[p.id] || {}} onChange={update(p.id)} onCrisis={() => setCrisisOpen(true)} nudge={p.id === nudgeId ? <KeepNudge onDismiss={dismissNudge} /> : null} hasVoice={voiced.includes(p.id)} />
              ))}
            </div>
            <SaveBar chapter={chapter} draft={draft} />
            {chapter.prompts.every((p) => draft[p.id]?.reflection) && <TrustpilotAsk chapterId="try-chapter-one" />}
          </>
        )}
      </main>
      <CrisisModal open={crisisOpen} onDismiss={() => setCrisisOpen(false)} />
    </div>
  );
}
